import { useEffect } from 'react';
import type { PhaseEntry } from '@/beta/types/showcase';

interface UsePreloadAdjacentPhasesOptions {
  phases: PhaseEntry[];
  activeIndex: number;
  /** How far on either side of activeIndex to preload. Default 2. */
  radius?: number;
}

/** Preloads phase images within ±`radius` of the active phase using
 *  `new Image()`. The browser caches the result so the next active swap
 *  hits paint immediately. We skip the active index (already rendered)
 *  and any neighbour with a null hero URL. Abandoned in-flight requests
 *  on unmount are fine — they finish into the cache. */
export function usePreloadAdjacentPhases(opts: UsePreloadAdjacentPhasesOptions): void {
  const { phases, activeIndex, radius = 2 } = opts;

  useEffect(() => {
    if (phases.length === 0) return;

    const min = Math.max(0, activeIndex - radius);
    const max = Math.min(phases.length - 1, activeIndex + radius);

    for (let i = min; i <= max; i++) {
      if (i === activeIndex) continue;
      const url = phases[i]?.hero_image_url;
      if (!url) continue;
      const img = new Image();
      img.src = url;
    }
    // Note: depending on the URL list itself would re-run preload on every
    // bundle refetch even if the active index is unchanged. Keying off
    // activeIndex + length is sufficient because phase URLs are stable
    // for a given vehicle.
  }, [phases, activeIndex, radius]);
}
